import React, { useState, useRef, useEffect } from 'react';

export default function DetailsBottomSheet({ node, treeData, isOpen, onClose }) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const sheetRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTranslateY(0);
    }
  }, [isOpen]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.clientY);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const delta = e.clientY - dragStart;
    if (delta > 0) {
      setTranslateY(delta);
    }
  };

  const handleMouseUp = (e) => {
    setIsDragging(false);
    const delta = e.clientY - dragStart;
    if (delta > 100) {
      onClose();
    } else {
      setTranslateY(0);
    }
  };

  if (!isOpen) return null;

  if (!node) {
    return (
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      >
        <div className="absolute bottom-0 left-0 right-0 bg-surface rounded-t-lg p-4 max-h-96">
          <p className="text-on-background/60 text-body-sm text-center">Select a node to view details</p>
        </div>
      </div>
    );
  }

  const ancestors = treeData.getAncestors(node.node_id);
  const sponsor = ancestors.length > 0 ? treeData.getNode(ancestors[ancestors.length - 1]) : null;
  const children = treeData.getChildren(node.node_id);

  return (
    <div
      className="fixed inset-0 bg-black/30 z-40"
      onClick={onClose}
    >
      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 bg-surface rounded-t-3xl shadow-lg max-h-96 overflow-hidden"
        style={{
          transform: `translateY(${translateY}px)`,
          transition: isDragging ? 'none' : 'transform 0.2s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle */}
        <div
          className="flex justify-center py-2 cursor-grab active:cursor-grabbing bg-surface border-b border-outline"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div className="w-12 h-1 bg-on-background/30 rounded-full"></div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-80 p-4 space-y-4">
          {/* User Info */}
          <div>
            <h4 className="text-body-sm font-medium text-on-background mb-2">User</h4>
            <div className="space-y-1">
              <div className="text-body-sm text-on-background font-medium">
                {node.user_name || node.user_email}
              </div>
              <div className="text-body-xs text-on-background/60 font-mono">
                {node.user_email}
              </div>
              <div className="text-body-xs text-on-background/60 font-mono bg-background p-2 rounded mt-2">
                {node.affiliate_code}
              </div>
            </div>
          </div>

          {/* Position Info */}
          <div className="border-t border-outline pt-4">
            <h4 className="text-body-sm font-medium text-on-background mb-2">Position</h4>
            <div className="grid grid-cols-3 gap-2 text-body-sm">
              <div className="text-center p-2 bg-background rounded">
                <div className="text-on-background/60 text-[10px]">Leg</div>
                <div className="font-bold text-primary">{node.leg_position}</div>
              </div>
              <div className="text-center p-2 bg-background rounded">
                <div className="text-on-background/60 text-[10px]">Level</div>
                <div className="font-bold text-primary">{node.depth}</div>
              </div>
              {node.top_leg && (
                <div className="text-center p-2 bg-background rounded">
                  <div className="text-on-background/60 text-[10px]">Top Leg</div>
                  <div className="font-bold text-primary">{node.top_leg}</div>
                </div>
              )}
            </div>
          </div>

          {/* Leg Sales Volume (Left/Middle/Right) -- live, computed from
              confirmed package purchases under each of this node's 3
              immediate legs. */}
          <div className="border-t border-outline pt-4">
            <h4 className="text-body-sm font-medium text-on-background mb-2">Leg Sales Volume</h4>
            <div className="grid grid-cols-3 gap-2 text-body-sm">
              <div className="text-center p-2 bg-background rounded">
                <div className="text-on-background/60 text-[10px]">Left (L)</div>
                <div className="font-bold text-primary">{Number(node.leg1_volume || 0).toFixed(2)}</div>
              </div>
              <div className="text-center p-2 bg-background rounded">
                <div className="text-on-background/60 text-[10px]">Middle (M)</div>
                <div className="font-bold text-primary">{Number(node.leg2_volume || 0).toFixed(2)}</div>
              </div>
              <div className="text-center p-2 bg-background rounded">
                <div className="text-on-background/60 text-[10px]">Right (R)</div>
                <div className="font-bold text-primary">{Number(node.leg3_volume || 0).toFixed(2)}</div>
              </div>
            </div>
          </div>

          {/* Sponsor */}
          {sponsor && (
            <div className="border-t border-outline pt-4">
              <h4 className="text-body-sm font-medium text-on-background mb-2">Sponsor</h4>
              <div className="bg-background p-2 rounded text-body-xs space-y-1">
                <div className="text-on-background font-medium">
                  {sponsor.user_name || sponsor.user_email}
                </div>
                <div className="text-on-background/60 font-mono">
                  {sponsor.affiliate_code}
                </div>
              </div>
            </div>
          )}

          {/* Downline */}
          {children.length > 0 && (
            <div className="border-t border-outline pt-4">
              <h4 className="text-body-sm font-medium text-on-background mb-2">
                Downline ({children.length})
              </h4>
              <div className="space-y-2">
                {children.slice(0, 5).map((childId) => {
                  const child = treeData.getNode(childId);
                  return (
                    <div key={childId} className="bg-background p-2 rounded text-body-xs">
                      <div className="text-on-background font-medium truncate">
                        {child.user_name || child.user_email}
                      </div>
                      <div className="text-on-background/60 font-mono text-[10px] truncate">
                        {child.affiliate_code}
                      </div>
                    </div>
                  );
                })}
                {children.length > 5 && (
                  <div className="text-body-xs text-on-background/60 text-center pt-2">
                    +{children.length - 5} more
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="border-t border-outline p-3">
          <button
            onClick={onClose}
            className="w-full py-2 bg-outline text-on-background rounded-lg font-medium text-body-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
